/**
 * Inside a library — the screen a student sits on for hours.
 *
 * *** FULL SCREEN, OUTSIDE THE ADMIN SHELL ***
 * Rendered as a sibling of `<AppShell>`, not inside it. An admin sidebar
 * beside a study room is the wrong frame entirely: while you are in the room
 * the only navigation that matters is leaving it.
 *
 * *** THE TILE GRID IS CAMERA-SHAPED FROM DAY ONE ***
 * Tiles are 4:3 slots holding an avatar today and a low-resolution camera
 * feed once LiveKit is wired. Built this way deliberately: a layout designed
 * around a list of names has nowhere to put video, and every pixel of it
 * would be thrown away. The slot, the name, the study time and the live dot
 * are all already where they will stay.
 *
 * *** THE VIDEO IS DELIBERATELY LOW QUALITY ***
 * This is presence, not video calling — seeing that other people are at
 * their desks. Resolution is the single biggest lever on bandwidth cost,
 * which is what decides whether the feature is viable at all.
 *
 * *** THE HEARTBEAT LIVES HERE NOW ***
 * It used to run on the lobby, which meant study time only accumulated
 * while that list was open. It belongs on the screen the student actually
 * sits on.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '@/components/ui/Toast'
import {
  sendLibraryHeartbeat,
  useLibraries,
  useLibraryOccupants,
} from '@/lib/queries'
import type { LibraryOccupant } from '@/types/api'
import { hoursMinutes, initials } from './format'
import { LibraryRoom } from './LibraryRoom'
import { useChatPlacement } from './useChatPlacement'
import { RoomLeaderboard } from './RoomLeaderboard'
import { OccupantCard } from './OccupantCard'
import styles from './LibraryRoomPage.module.css'

/** Fallback only. The real value arrives from the server on join. */
const DEFAULT_HEARTBEAT_SECONDS = 30

export function LibraryRoomPage() {
  const { libraryId = '' } = useParams<{ libraryId: string }>()
  const navigate = useNavigate()
  const toast = useToast()

  const { data: list } = useLibraries()
  const { data: occupantData } = useLibraryOccupants(libraryId)

  const card = list?.libraries.find((l) => l.library_id === libraryId) ?? null
  const occupants = occupantData?.occupants ?? []

  const myRow = occupants.find((o) => o.is_mine) ?? null
  /*
    *** FROM THE VISIT, NOT THE ACCOUNT ***
    The occupant row says which capacity this person joined in. A mentor who
    ever sits as a student would otherwise see the supervision view on a
    visit where they are studying.
  */
  const iAmMentor = myRow?.role === 'MENTOR'

  /*
    Split rather than sorted. Mentors are named in their own strip and
    students fill the tile grid: a mentor in the grid implies they occupy
    one of the seats the count is about, and they do not.
  */
  const mentors = occupants.filter((o) => o.role === 'MENTOR')
  const students = occupants.filter((o) => o.role === 'STUDENT')

  const [selected, setSelected] = useState<LibraryOccupant | null>(null)
  const [heartbeatSeconds, setHeartbeatSeconds] = useState(
    DEFAULT_HEARTBEAT_SECONDS,
  )

  /*
    *** THE BREAK COUNTDOWN COMES FROM THE SERVER ***
    Held in state and refreshed by every heartbeat response. A purely local
    timer would drift, and the moment it disagreed with the sweeper a
    student would be watching 2:00 remaining on a seat already given away.
  */
  /*
    Armed only once the server has confirmed this person is inside. Until
    then a list that says otherwise is assumed to predate the join.
  */
  const wasInside = useRef(false)

  const [breakLeft, setBreakLeft] = useState<number | null>(null)
  const { placement, hidden, setPlacement, setHidden } = useChatPlacement()



  const onBreak = breakLeft !== null

  /*
    *** THROWN OUT OF A ROOM YOU ARE NOT IN ***
    The idle sweeper can close a visit while the tab sat in the background,
    and the API refuses every call from outside the room. Without this the
    screen would sit there showing errors it cannot recover from.

    Waits for the list to load: `card` is null for the first moment of every
    visit, and redirecting on that would bounce out of a room just entered.
  */
  useEffect(() => {
    if (!list) return
    /*
      *** NEVER ON THE FIRST SNAPSHOT AFTER ARRIVING ***
      Even with the join awaiting its refetch, a slow network or a
      background refetch in flight can leave this screen holding a list
      fetched BEFORE the join. Acting on it ejects someone from a room they
      just entered. One confirmed sighting of `i_am_inside` before the
      guard arms means a stale snapshot can only ever delay the redirect,
      never cause a false one.
    */
    if (card?.i_am_inside) {
      wasInside.current = true
      return
    }
    if (card && !wasInside.current) return
    if (card && !card.i_am_inside) {
      /*
        Naming the reason matters: "you are no longer in this room" after a
        break that ran out reads as the app losing their seat, rather than
        as the thirty minutes they were told about running out.
      */
      toast.info(
        onBreak
          ? 'Your break ran out, so your seat was released.'
          : 'You are no longer in this room.',
      )
      navigate('/library', { replace: true })
    }
  }, [list, card, navigate, toast, onBreak])

  /*
    *** ONLY WHILE VISIBLE ***
    A background tab that kept checking in would record study time for
    somebody who walked away, which is the inflation the server's cap exists
    to prevent, arriving by a different route.
  */
  useEffect(() => {
    if (!libraryId) return
    let cancelled = false

    const beat = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const visit = await sendLibraryHeartbeat()
        if (cancelled) return
        if (visit.heartbeat_seconds > 0) {
          setHeartbeatSeconds(visit.heartbeat_seconds)
        }
        // The authoritative figure. Also how the screen learns the break
        // ended while the tab was in the background.
        setBreakLeft(visit.break_seconds_left)
      } catch {
        /*
          Swallowed on purpose. A student is studying, not operating the app;
          a toast every thirty seconds over a flaky connection would be worse
          than the lost minute. The idle sweep closes the visit if the
          silence lasts, crediting time up to the last heartbeat.
        */
      }
    }

    void beat()
    const timer = window.setInterval(beat, heartbeatSeconds * 1000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [libraryId, heartbeatSeconds])





  return (
    <div className={styles.page}>
      {/*
        *** THE RAIL IS THE SIDEBAR NOW ***
        Its controls render in the shell's dark column in place of the
        navigation — see LibraryFocusRail. One left column, not two.
      */}

      {/* -------------------------------------------------- centre: room */}
      <main className={styles.main}>
        <header className={styles.head}>
          <h1 className={styles.title}>{card?.name ?? 'Library'}</h1>
          <p className={styles.sub}>
            {card
              ? `${card.students_inside} studying · ${
                  card.capacity - card.students_inside
                } seats free · Silent room`
              : 'Silent room'}
          </p>
        </header>

        {/*
          *** PINNED: IN THE COLUMN, UNDER THE ROOM HEADER ***
          The order is where you are, what people are saying, who is on
          duty, who is here. Rendered in place rather than reordered with
          CSS, so the DOM order matches the reading order for a screen
          reader.
        */}
        {placement === 'pin' && (
          <LibraryRoom
            libraryId={libraryId}
            placement={placement}
            onPlacement={setPlacement}
          />
        )}

        {/*
          *** WHO IS ON DUTY, NAMED ***
          A student needs to know a mentor is reachable before deciding to
          ask; a mentor needs to know which colleagues are already in the
          room. Both read the same strip. It sits ABOVE the grid because in
          a room of forty tiles the one mentor is otherwise just another
          square.
        */}
        {/*
          *** ALWAYS RENDERED, EVEN WITH NOBODY ON DUTY ***
          Hiding the card left a student unable to tell "no mentor is here
          right now" from "this room has no mentors at all" — and the first
          is temporary while the second would change whether they wait.
        */}
        <section
          className={`${styles.dutyBlock} ${
            mentors.length === 0 ? styles.dutyEmpty : ''
          }`}
        >
          <h2 className={styles.blockTitle}>
            {mentors.length === 1 ? 'Mentor on duty' : 'Mentors on duty'}
            {mentors.length > 1 && (
              <span className={styles.count}>{mentors.length}</span>
            )}
          </h2>

          {mentors.length === 0 ? (
            <p className={styles.dutyNone}>
              {/*
                Only a student reaches this: a mentor viewing the room is
                themselves in `mentors`, so the list is never empty for
                them. Says what to do, not just what is missing.
              */}
              No mentor is in the room at the moment. Post your question in
              the chat and it will be answered when one arrives.
            </p>
          ) : (
            <ul className={styles.dutyList}>
              {mentors.map((mentor) => (
                <li key={mentor.user_id}>
                  <button
                    type="button"
                    className={styles.duty}
                    onClick={() => setSelected(mentor)}
                  >
                    <span className={styles.dutyAvatar}>
                      {initials(mentor.name)}
                    </span>
                    <span className={styles.dutyText}>
                      <span className={styles.dutyName}>
                        {mentor.name}
                        {mentor.is_mine && (
                          <span className={styles.dutyYou}>You</span>
                        )}
                      </span>
                      <span className={styles.dutyTime}>
                        here {hoursMinutes(mentor.active_seconds)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.peopleBlock}>
          <h2 className={styles.blockTitle}>
            {/* Students only: the mentors are named above, and counting
                them here would imply they take a seat. */}
            {iAmMentor ? 'Students here' : 'People here'}
            <span className={styles.count}>{students.length}</span>
          </h2>

          {students.length === 0 ? (
            <p className={styles.empty}>
              {iAmMentor
                ? 'No students are in the room yet.'
                : 'Nobody else is here yet. The room fills up through the day.'}
            </p>
          ) : (
            <ul className={styles.tiles}>
              {students.map((person) => (
                <li key={person.user_id}>
                  {/* Always a student: mentors are named in the duty strip
                      above and never appear in this grid, because a tile
                      here implies one of the seats the count is about. */}
                  <button
                    type="button"
                    className={styles.tile}
                    onClick={() => setSelected(person)}
                  >
                    {/*
                      The camera slot. An avatar sits in it now; a low-res
                      feed will sit in exactly the same box later, which is
                      why it holds its aspect ratio even while empty.
                    */}
                    <span className={styles.slot}>
                      <span className={styles.avatar}>
                        {initials(person.name)}
                      </span>
                      <span className={styles.liveDot} aria-hidden="true" />
                    </span>
                    <span className={styles.tileName}>{person.name}</span>
                    <span className={styles.tileTime}>
                      {hoursMinutes(person.active_seconds)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

      </main>

      {/* --------------------------------------------- right: the board */}
      <aside className={styles.board}>
        <RoomLeaderboard viewerIsMentor={iAmMentor} />
      </aside>

      {/*
        *** DOCKED: A FLOATING CARD OVER THE ROOM ***
        Outside the grid so it never takes a column, and hideable — a
        silent study room should let someone put the conversation away
        entirely.
      */}
      {placement === 'dock' && !hidden && (
        <div className={styles.dock}>
          <LibraryRoom
            libraryId={libraryId}
            placement={placement}
            onPlacement={setPlacement}
            onHide={() => setHidden(true)}
          />
        </div>
      )}

      {placement === 'dock' && hidden && (
        <button
          type="button"
          className={styles.pill}
          onClick={() => setHidden(false)}
        >
          Room chat
        </button>
      )}

      {selected && (
        <OccupantCard
          person={selected}
          libraryId={libraryId}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
